import { TextLink } from '@manulife/mux';
import parse from 'html-react-parser';
import React from 'react';
import useShallowEqualSelector from './useShallowEqualSelector';

export const FRAGMENT_MP = {
    bankInfo: 'mp-bankinfo',
    bankValidationErrors: 'mp-bankvalidationerrors',
    common: 'mp-common',
    contactInfo: 'mp-contactinfo',
    continueToClaimsButton: 'continueToClaimsButton',
    countries: 'mp-countries',
    email: 'mp-email',
    familyMember: 'mp-familymember',
    landing: 'mp-landing',
    provinces: 'mp-provinces',
    timeoutModal: 'mp-timeoutmodal',
    validationMessages: 'mp-validationmessages',
    whoIsCovered: 'mp-whoiscovered',
};

export const FRAGMENT_GB_SHARED = {
    cscPhoneNumber: 'gbpm-cscphonenumber',
};

export const FRAGMENT_MEMBER_EXP = {
    memberCommon: 'member-common',
};

/**
 * Hook to get aem content given a fragment name and a field name.
 *
 * Options (all optional):
 *  -options.fallback.fragmentName/options.fallback.field:
 *      -The content to use if the provided fragment/field is not present
 *  -options.replaceText:
 *      -Array of key/value pairs. Uses a regular expression to replace each key with it's matching value.
 *      -Key should be passed as JSX if you are replacing with markup
 *      -Options.parseHTML is automatically enabled when this option is utilized.
 *  -options.parseHTML:
 *      -Parses the html of the content so it can be output into JSX
 *
 * @param {string} fragmentName
 * @param {string} field
 * @param {string} options
 */
const useAEMContent = (fragmentName, field, options = {}) => {
    const aemContent = useShallowEqualSelector((state) => state.AEMContent.data);

    // Fallback pattern
    let content = '';
    if (aemContent && aemContent[fragmentName] && aemContent[fragmentName][field]) {
        content = aemContent[fragmentName][field];
    } else if (
        options.fallback &&
        aemContent &&
        aemContent[options.fallback.fragmentName] &&
        aemContent[options.fallback.fragmentName][options.fallback.field]
    ) {
        content = aemContent[options.fallback.fragmentName][options.fallback.field];
    }

    // Prepare text to be replaced by putting it in <template> tags
    const replaceMap = {};
    if (options.replaceText) {
        options.replaceText.forEach((replacePair, index) => {
            // If content is a string, do simple replace.
            // In situations where we need to use the text such as an aria-label,
            // the replaced text will show up as an object if simple replace is not done.
            if (typeof replacePair.value === 'string' || replacePair.value instanceof String) {
                content = content.replace(new RegExp(replacePair.key, 'g'), replacePair.value);
            } else {
                content = content.replace(
                    new RegExp(replacePair.key, 'g'),
                    `<span key="stringReplace-${index}">${replacePair.key}</span>`
                );
            }
            replaceMap[replacePair.key] = replacePair.value;
        });
    }

    // Parse text into HTML using html-react-parser
    if (options.parseHTML || options.replaceText) {
        // Tracking an index will give us an easy way to create unique id's for each element
        // This is tracked manually since the replace function does not track an index itself
        let index = 0;
        content = parse(content, {
            replace: (node) => {
                // Handle replacing of text and JSX
                // Note that we are adding the index to the key... this guarantees there are no duplicate keys caused
                // by the same string being replaced twice
                if (node.type === 'tag' && node.name === 'span' && replaceMap[node.children[0]?.data]) {
                    return (
                        <React.Fragment key={`${node.attribs.key}-${index}`}>
                            {replaceMap[node.children[0]?.data]}
                        </React.Fragment>
                    );
                }

                // Display link that opens in a new tab with the 'external' icon
                if (node.type === 'tag' && node.name === 'a' && node.attribs.target === '_blank') {
                    return (
                        <React.Fragment key={`externalLink-${index}`}>
                            <TextLink
                                id={`externalLink-${index}`}
                                data-testid="externalLink"
                                to={node.attribs.href}
                                isExternal
                            >
                                {node.children[0]?.data}
                            </TextLink>
                        </React.Fragment>
                    );
                }
                index++;
            },
        });
    }

    return content;
};

export default useAEMContent;
